import type { ProjectCockpitDisplayViewModel } from "../viewmodel/projectCockpitAdapter";
import type { ProjectReadinessV1 } from "@automatry/schemas";
import type { ScreenId } from "../nav";
import { lazy, Suspense } from "react";
import { plainCatalogRelease, plainRuleSet } from "../app/format";
import { AiProvenanceBadge, cx } from "./shared";
import { GlossaryPanel, Term, type GlossaryTermName } from "./Glossary";

const EngineRunPanel = lazy(async () => {
  const module = await import("../features/engine-progress/EngineRunPanel");

  return { default: module.EngineRunPanel };
});

const stepTone = {
  done: {
    ring: "border-[#4E7644]",
    bg: "bg-[#4E7644]",
    mark: "text-white",
    text: "text-[var(--ink)]"
  },
  active: {
    ring: "border-[var(--ink)]",
    bg: "bg-[var(--ink)]",
    mark: "text-[var(--onSolid)]",
    text: "text-[var(--ink)]"
  },
  todo: {
    ring: "border-[var(--line3)]",
    bg: "bg-[var(--surface)]",
    mark: "text-[var(--faint)]",
    text: "text-[var(--faint)]"
  },
  blocked: {
    ring: "border-[#BC4A3C]",
    bg: "bg-[#F6E7E4]",
    mark: "text-[#BC4A3C]",
    text: "text-[#BC4A3C]"
  }
} as const;

const scopeStatusTone = {
  included: {
    label: "Included",
    className: "bg-[#EAF1E6] text-[#4E7644]"
  },
  by_others: {
    label: "By others",
    className: "bg-[#ECEFF3] text-[#5E6E82]"
  },
  excluded: {
    label: "Excluded",
    className: "bg-[var(--line2)] text-[var(--muted2)]"
  }
} as const;

const inputStatusTone = {
  accepted: {
    label: "Accepted",
    dot: "#4E7644",
    text: "text-[#4E7644]"
  },
  review: {
    label: "Review",
    dot: "#A8823E",
    text: "text-[#8A6A2E]"
  },
  missing: {
    label: "Missing",
    dot: "#BC4A3C",
    text: "text-[#BC4A3C]"
  },
  not_produced: {
    label: "Pending",
    dot: "var(--muted2)",
    text: "text-[var(--muted2)]"
  }
} as const;

const plainProjectDescription =
  "This project overview shows the specification summary, the project evidence behind it, and the items ready for review.";

const overviewGlossaryTerms = [
  "RFI",
  "BMS",
  "FCU",
  "UFH",
  "BOM",
  "IO",
  "Edge",
  "Netmaster",
  "Modbus",
  "point",
  "point ledger",
  "controller",
  "device",
  "zone",
  "port",
  "terminal",
  "terminate",
  "relay",
  "PSU",
  "RDU",
  "take-off",
  "mapping schedule",
  "commissioning",
  "verifier"
] satisfies readonly GlossaryTermName[];

const plainInternalProcessCopy = (value: string): string =>
  value
    .replace(/\blane[-\s]?a\b/gi, "AI review")
    .replace(/\blane[-\s]?b\b/gi, "AI specification");

const plainInputMeta = (meta: string): string =>
  plainInternalProcessCopy(meta)
    .replace("passed to runProject(specText)", "used by the AI")
    .replace("passed to AI review", "used by the AI")
    .replace("passed to AI specification", "used by the AI");

const pluralLabel = (
  count: number,
  singular: string,
  plural = `${singular}s`
): string => `${count} ${count === 1 ? singular : plural}`;

const apartmentHomeLabel = (
  apartment: ProjectCockpitDisplayViewModel["apartmentTypes"][number]
): string => {
  const bedLabel =
    apartment.beds === 0
      ? "Studio"
      : apartment.beds === undefined
        ? "Bedroom count pending"
        : `${apartment.beds} ${apartment.beds === 1 ? "bed" : "beds"}`;

  return `${bedLabel} · ×${apartment.quantity} ${
    apartment.quantity === 1 ? "home" : "homes"
  }`;
};

function ProjectTrustPanel({
  cockpit,
  projectNames
}: {
  cockpit: ProjectCockpitDisplayViewModel;
  projectNames: readonly string[];
}) {
  return (
    <details className="mt-[30px] rounded-[13px] border border-[#D7E1EA] bg-[#F6FAFD] px-5 py-4">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[10.5px] font-bold uppercase tracking-[.12em] text-[#3E6E9A] [&::-webkit-details-marker]:hidden">
        <span>How this was checked</span>
        <span className="text-[11px] normal-case tracking-normal text-[var(--muted)]">
          Product, rules and comparison evidence
        </span>
      </summary>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4 border-t border-[#D7E1EA] pt-4">
        <div className="min-w-[280px] flex-1">
          <p className="m-0 mt-2 max-w-[760px] text-[12.5px] leading-[1.55] text-[var(--ink2)]">
            The AI drafts the specification from the project inputs, the baseline rules,
            and the active products and price list. Rule-based checks then verify that
            the draft can be reviewed and exported; benchmark comparisons are there to
            spot likely gaps, not to invent a headline score.
          </p>
        </div>
        <div className="grid min-w-[260px] gap-1.5 text-[11px] font-bold text-[var(--muted)]">
          <span>
            Product and pricing:{" "}
            <span className="text-[var(--ink2)]">
              {plainCatalogRelease(cockpit.activeCatalogReleaseId)}
            </span>
          </span>
          <span>
            Review rules:{" "}
            <span className="text-[var(--ink2)]">
              {plainRuleSet(cockpit.activeRuleSetId)}
            </span>
          </span>
          <span>
            AI draft: <span className="text-[var(--ink2)]">Available for review</span>
          </span>
        </div>
      </div>
      <div className="mt-4 border-t border-[#D7E1EA] pt-3">
        <div className="mb-2 text-[9.5px] font-extrabold uppercase tracking-[.1em] text-[var(--faint)]">
          Compared with completed projects · {projectNames.length}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {projectNames.map((name, index) => (
            <span
              key={`${name}-${index}`}
              className="rounded-md bg-[var(--surface)] px-2.5 py-1 text-[10.5px] font-bold text-[#3E6E9A]"
            >
              {name}
            </span>
          ))}
        </div>
        <div className="mt-2 text-[11.5px] font-semibold leading-[1.45] text-[var(--faint)]">
          These named projects are used as comparison examples. They help check whether
          the AI's product quantity list, or <Term name="BOM">BOM</Term>, looks
          plausible against known delivered jobs. This screen does not claim a
          percentage match.
        </div>
      </div>
      <div className="mt-4">
        <GlossaryPanel terms={overviewGlossaryTerms} />
      </div>
    </details>
  );
}

export function ProjectOverview({
  projectId,
  cockpit,
  readiness,
  projectNames,
  onOpenSpec,
  onNav
}: {
  projectId?: string;
  cockpit: ProjectCockpitDisplayViewModel;
  readiness?: ProjectReadinessV1 | null;
  projectNames: readonly string[];
  onOpenSpec: (apartmentTypeId: string) => void;
  onNav: (screen: ScreenId) => void;
}) {
  const firstApartmentTypeId =
    cockpit.apartmentTypes[0]?.apartmentTypeId ?? "carrington-type-a";
  const issueStatus =
    readiness?.clientIssue.status === "ready"
      ? "Ready to issue"
      : readiness?.clientIssue.status === "blocked"
        ? "Not ready to issue"
        : "Needs attention";
  const readinessSteps =
    readiness?.progress.steps ??
    (["inputs", "requirements", "spec", "review", "scope", "export"] as const).map(
      (id) => ({
        id,
        label:
          id === "spec" ? "Specification" : id.replace(/^./, (c) => c.toUpperCase()),
        status: "blocked" as const
      })
    );
  const readinessAttention = [
    {
      id: "prices",
      count: readiness?.counts.byClass.missing_catalogue_prices.open ?? null,
      label: "Prices missing",
      hint: "Catalogue or reviewed manual prices needed for a priced issue.",
      target: "quote" as const,
      accent: "#BC4A3C"
    },
    {
      id: "downstream",
      count: readiness?.counts.byClass.incomplete_downstream_rows.open ?? null,
      label: "Items incomplete",
      hint: "Room, controller, terminal or panel-power details need review.",
      target: "mapping" as const,
      accent: "#A8823E"
    },
    {
      id: "assumptions",
      count: readiness?.counts.byClass.unapproved_assumptions.open ?? null,
      label: "Assumptions to accept",
      hint: "Accepted assumptions move into the client CEA schedule.",
      target: "risks" as const,
      accent: "#6A5E97"
    }
  ];

  return (
    <div className="flex-1 overflow-auto px-8 py-[34px] pb-[76px] xl:px-14">
      <div className="mx-auto max-w-[1240px]">
        <div className="flex flex-wrap items-start justify-between gap-8">
          <div className="min-w-[280px] flex-1">
            <div className="flex items-center gap-[9px]">
              <span className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
                Project overview
              </span>
              <span className="rounded-full bg-[var(--chip)] px-[9px] py-0.5 text-[10px] font-bold text-[var(--faint)]">
                AI draft
              </span>
            </div>
            <h1 className="m-0 mt-[7px] max-w-[860px] text-[32px] font-extrabold leading-[1.08] tracking-[-.035em] text-[var(--ink)]">
              {cockpit.title}
            </h1>
            <p className="m-0 mt-2 max-w-[780px] text-sm leading-[1.5] text-[var(--muted)]">
              {plainProjectDescription}
            </p>
            {cockpit.partialScope ? (
              <div className="mt-3 inline-flex max-w-[780px] items-center rounded-[8px] border border-[#E7D09A] bg-[#FBF7EF] px-3 py-2 text-[12px] font-bold leading-[1.35] text-[#7B5818]">
                {cockpit.partialScope.message}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            data-showme="overview-continue"
            onClick={() => onOpenSpec(firstApartmentTypeId)}
            className="mt-6 flex flex-none cursor-pointer items-center gap-[9px] rounded-[11px] bg-[var(--ink)] px-5 py-[13px] text-[13px] font-bold text-[var(--onSolid)]"
          >
            Continue to specification
          </button>
        </div>

        <div className="mt-5 flex flex-wrap gap-[9px]">
          {[
            ["Project", cockpit.projectLabel],
            ["Requirements agreed", String(cockpit.approvedRequirementCount)],
            ["Issue status", issueStatus],
            [
              "Progress",
              readiness ? `${readiness.progress.percent}%` : "Needs attention"
            ],
            [
              "Apartment types",
              cockpit.partialScope
                ? `${cockpit.apartmentTypes.length} verified`
                : String(cockpit.apartmentTypes.length)
            ]
          ].map(([key, value]) => (
            <label
              key={key}
              className="flex items-center gap-[9px] rounded-[10px] border border-[var(--line)] bg-[var(--surface)] px-[13px] py-2"
            >
              <span className="flex-none text-[10px] font-bold uppercase tracking-[.05em] text-[var(--faint)]">
                {key}
              </span>
              <input
                value={value}
                readOnly
                className="w-32 border-none bg-transparent p-0 text-[12.5px] font-bold capitalize text-[var(--ink2)] outline-none"
              />
            </label>
          ))}
        </div>

        {projectId ? (
          <Suspense fallback={null}>
            <EngineRunPanel projectId={projectId} />
          </Suspense>
        ) : null}

        <section className="mt-[30px] rounded-2xl border border-[var(--line)] bg-[var(--surface)] px-8 pb-6 pt-[26px]">
          <div className="mb-[22px] text-[10.5px] font-bold uppercase tracking-[.12em] text-[var(--faint)]">
            Where this job is
          </div>
          <div className="relative mx-auto flex max-w-[900px]">
            <div className="absolute left-[10%] right-[10%] top-[18px] z-0 h-0.5 bg-[var(--line3)]" />
            {readinessSteps.map((step, index) => {
              const state =
                step.status === "done"
                  ? "done"
                  : step.status === "blocked"
                    ? "blocked"
                    : step.status === "in_progress" || step.status === "ready"
                      ? "active"
                      : "todo";
              const tone = stepTone[state];
              const target: ScreenId =
                step.id === "review"
                  ? "risks"
                  : step.id === "scope"
                    ? "scope"
                    : step.id;

              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => onNav(target)}
                  className="relative z-[1] flex flex-1 cursor-pointer flex-col items-center"
                >
                  <span
                    className={cx(
                      "flex h-[38px] w-[38px] items-center justify-center rounded-full border-2 text-sm font-extrabold",
                      tone.ring,
                      tone.bg,
                      tone.mark
                    )}
                  >
                    {state === "done" ? "✓" : state === "blocked" ? "!" : index + 1}
                  </span>
                  <span className={cx("mt-2.5 text-[13px] font-extrabold", tone.text)}>
                    {step.label}
                  </span>
                  <span className="mt-0.5 text-[10.5px] font-bold text-[var(--faint)]">
                    {step.status.replaceAll("_", " ")}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <div className="mx-0.5 mb-[13px] mt-[34px] text-[10.5px] font-bold uppercase tracking-[.12em] text-[var(--faint)]">
          Needs your attention
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {readinessAttention.map((attention) => (
            <button
              key={attention.id}
              type="button"
              onClick={() => onNav(attention.target)}
              className="cursor-pointer rounded-[13px] border border-l-[3px] border-[var(--line)] bg-[var(--surface)] px-5 py-[19px] text-left"
              style={{ borderLeftColor: attention.accent }}
            >
              <div className="flex items-baseline gap-[9px]">
                <span
                  className="text-[34px] font-extrabold leading-none tracking-[-.04em]"
                  style={{ color: attention.accent }}
                >
                  {attention.count ?? "—"}
                </span>
                <span className="text-[13px] font-extrabold text-[var(--ink2)]">
                  {attention.label}
                </span>
              </div>
              <div className="mt-[9px] text-xs leading-[1.45] text-[var(--muted2)]">
                {attention.hint}
              </div>
              <div className="mt-[11px] text-[11.5px] font-bold text-[var(--muted)]">
                Review →
              </div>
            </button>
          ))}
        </div>

        <div className="mt-[13px] grid gap-[13px] xl:grid-cols-2">
          <section className="rounded-[14px] border border-[var(--line)] bg-[var(--surface)] px-5 py-[18px]">
            <div className="mb-3.5 flex items-center justify-between">
              <div className="text-[13.5px] font-extrabold">What we're providing</div>
              <button
                type="button"
                onClick={() => onNav("scope")}
                className="text-[11.5px] font-bold text-[var(--muted)]"
              >
                Scope →
              </button>
            </div>
            <div className="mb-3.5 flex gap-2">
              <span className="flex-1 rounded-[9px] bg-[#EAF1E6] py-[9px] text-center">
                <span className="block text-xl font-extrabold text-[#4E7644]">
                  {cockpit.scopeCounts.included}
                </span>
                <span className="text-[10.5px] font-bold text-[#4E7644]">Included</span>
              </span>
              <span className="flex-1 rounded-[9px] bg-[#ECEFF3] py-[9px] text-center">
                <span className="block text-xl font-extrabold text-[#5E6E82]">
                  {cockpit.scopeCounts.byOthers}
                </span>
                <span className="text-[10.5px] font-bold text-[#5E6E82]">
                  By others
                </span>
              </span>
              <span className="flex-1 rounded-[9px] bg-[var(--line2)] py-[9px] text-center">
                <span className="block text-xl font-extrabold text-[var(--muted2)]">
                  {cockpit.scopeCounts.excluded}
                </span>
                <span className="text-[10.5px] font-bold text-[var(--muted2)]">
                  Excluded
                </span>
              </span>
            </div>
            <div className="flex flex-col gap-px">
              {cockpit.scopeRows.slice(0, 5).map((row) => {
                const tone = scopeStatusTone[row.status];

                return (
                  <div
                    key={row.id}
                    className="flex items-center gap-2.5 border-t border-[var(--line2)] py-2"
                  >
                    <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-[var(--ink2)]">
                      {plainInternalProcessCopy(row.title)}
                    </span>
                    <span
                      className={cx(
                        "flex-none rounded-full px-[9px] py-[3px] text-[10.5px] font-bold",
                        tone.className
                      )}
                    >
                      {tone.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="rounded-[14px] border border-[var(--line)] bg-[var(--surface)] px-5 py-[18px]">
            <div className="mb-3.5 flex items-center justify-between">
              <div className="text-[13.5px] font-extrabold">Inputs received</div>
              <button
                type="button"
                onClick={() => onNav("inputs")}
                className="text-[11.5px] font-bold text-[var(--muted)]"
              >
                Inputs →
              </button>
            </div>
            <div className="flex flex-col gap-px">
              {cockpit.inputRows.map((input) => {
                const tone = inputStatusTone[input.status];

                return (
                  <div
                    key={input.id}
                    className="flex items-center gap-[11px] border-t border-[var(--line2)] py-2.5"
                  >
                    <span className="w-[38px] flex-none rounded-[5px] bg-[var(--line2)] px-1.5 py-[3px] text-center text-[8.5px] font-extrabold tracking-[.04em] text-[var(--muted)]">
                      {input.kind}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12.5px] font-bold text-[var(--ink2)]">
                        {plainInternalProcessCopy(input.name)}
                      </span>
                      <span className="mt-px block truncate text-[11px] text-[var(--faint)]">
                        {plainInputMeta(input.meta)}
                      </span>
                    </span>
                    <span className="flex flex-none items-center gap-1.5">
                      <span
                        className="h-[7px] w-[7px] rounded-full"
                        style={{ background: tone.dot }}
                      />
                      <span className={cx("text-[10.5px] font-bold", tone.text)}>
                        {tone.label}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <div className="mx-0.5 mb-[11px] mt-[30px] flex items-center justify-between">
          <div className="text-[10.5px] font-bold uppercase tracking-[.12em] text-[var(--faint)]">
            Apartment types
          </div>
          <div className="text-[11.5px] font-semibold text-[var(--faint)]">
            Click a type to open its specification
          </div>
        </div>
        <div className="grid gap-[13px] sm:grid-cols-2 xl:grid-cols-4">
          {cockpit.apartmentTypes.map((apartment) => (
            <button
              key={apartment.apartmentTypeId}
              type="button"
              onClick={() => onOpenSpec(apartment.apartmentTypeId)}
              className="cursor-pointer rounded-[14px] border border-[var(--line)] bg-[var(--surface)] p-[17px] text-left transition hover:-translate-y-0.5 hover:border-[var(--line3)] hover:shadow-[0_12px_28px_-24px_rgba(35,33,30,.8)]"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-[11px] bg-[var(--ink)] text-[17px] font-extrabold text-[var(--onSolid)]">
                  {apartment.label.replace(/^Type /, "").slice(0, 1)}
                </span>
                {apartment.hasOpenRisk ? (
                  <span className="rounded-full bg-[#F6E7E4] px-2 py-[3px] text-[10px] font-bold text-[#BC4A3C]">
                    ● Risk
                  </span>
                ) : apartment.hasOpenDecision ? (
                  <span className="rounded-full bg-[#F6EEDD] px-2 py-[3px] text-[10px] font-bold text-[#8A6A2E]">
                    ● Decision
                  </span>
                ) : null}
              </div>
              <div className="mt-[13px] text-base font-extrabold tracking-[-.02em]">
                {apartment.label}
              </div>
              <div className="mt-px text-[11.5px] font-semibold text-[var(--faint)]">
                {apartmentHomeLabel(apartment)}
              </div>
              <div className="mt-[13px] flex flex-wrap gap-1.5">
                <span className="rounded-md bg-[var(--line2)] px-2 py-0.5 text-[10.5px] font-bold text-[var(--muted)]">
                  <Term name="FCU">{pluralLabel(apartment.fcuCount, "FCU")}</Term>
                </span>
                <span className="rounded-md bg-[var(--line2)] px-2 py-0.5 text-[10.5px] font-bold text-[var(--muted)]">
                  <Term name="controller">
                    {pluralLabel(apartment.controllerCount, "controller")}
                  </Term>
                </span>
                {apartment.hasEdgeFeature ? (
                  <span className="rounded-md bg-[#EFE6DA] px-2 py-0.5 text-[10.5px] font-bold text-[#8A6A2E]">
                    <Term name="Edge">Edge</Term>
                  </span>
                ) : null}
              </div>
              {apartment.hasEdgeFeature ? (
                <div className="mt-3 rounded-[10px] border border-[#D7E1EA] bg-[#F6FAFD] px-3 py-2">
                  <div className="text-[9.5px] font-extrabold uppercase tracking-[.08em] text-[#3E6E9A]">
                    Edge capabilities
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {apartment.edgeCapabilities.slice(0, 3).map((capability) => (
                      <span
                        key={`${apartment.apartmentTypeId}-${capability}`}
                        className="rounded-md bg-[var(--surface)] px-2 py-0.5 text-[10px] font-bold text-[var(--muted)]"
                      >
                        {capability}
                      </span>
                    ))}
                  </div>
                  <div className="mt-2">
                    <AiProvenanceBadge
                      why={apartment.edgeInclusionWhy}
                      where={apartment.edgeInclusionWhere}
                      defaulted={apartment.edgeDefaultApplied}
                      compact={!apartment.edgeDefaultApplied}
                      ariaHidden
                    />
                  </div>
                </div>
              ) : null}
            </button>
          ))}
        </div>

        <ProjectTrustPanel cockpit={cockpit} projectNames={projectNames} />
      </div>
    </div>
  );
}
